/** @format */

import { useState, useEffect, useCallback } from "react";
import { collection, getDocs, query, orderBy, limit } from "firebase/firestore";
import { db } from "../firebase/config";
import { getCollectionCount } from "../utils/firestoreCounters";

const useBookReviewStats = () => {
  const [bookReviewCounts, setBookReviewCounts] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch review counts for all books using counters
  const fetchBookReviewCounts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Get books with limits instead of all books
      const booksQuery = query(
        collection(db, "books"),
        orderBy("createdAt", "desc"),
        limit(50) // Limit to 50 books instead of ALL
      );

      const booksSnapshot = await getDocs(booksQuery);
      const books = booksSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      const counts = {};

      // Process books in smaller batches to avoid overwhelming Firestore
      const batchSize = 5; // Reduced batch size
      for (let i = 0; i < books.length; i += batchSize) {
        const batch = books.slice(i, i + batchSize);

        const batchPromises = batch.map(async (book) => {
          try {
            // Try to get count from counter first
            const counterKey = `books_${book.id}_reviews`;
            let count = 0;

            try {
              count = await getCollectionCount(counterKey);
            } catch (err) {
              // If counter doesn't exist, fall back to subcollection count
              const reviewsRef = collection(db, "books", book.id, "reviews");
              const reviewsQuery = query(reviewsRef, limit(1)); // Just check if exists
              const reviewsSnapshot = await getDocs(reviewsQuery);

              if (!reviewsSnapshot.empty) {
                // If we have reviews, get a sample to estimate count
                const sampleQuery = query(
                  reviewsRef,
                  orderBy("createdAt", "desc"),
                  limit(10)
                );
                const sampleSnapshot = await getDocs(sampleQuery);
                count = sampleSnapshot.size; // This is just an estimate
              }
            }

            counts[book.id] = count;
          } catch (err) {
            // If there's an error, set count to 0
            counts[book.id] = 0;
          }
        });

        await Promise.all(batchPromises);

        // Small delay between batches to respect Firestore limits
        if (i + batchSize < books.length) {
          await new Promise((resolve) => setTimeout(resolve, 200)); // Increased delay
        }
      }

      setBookReviewCounts(counts);
    } catch (err) {
      console.error("Error fetching book review counts:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  // Get review count for a specific book
  const getReviewCountForBook = useCallback(
    (bookId) => {
      return bookReviewCounts[bookId] || 0;
    },
    [bookReviewCounts]
  );

  // Refresh review counts
  const refreshCounts = useCallback(() => {
    fetchBookReviewCounts();
  }, [fetchBookReviewCounts]);

  // Load review counts on mount
  useEffect(() => {
    fetchBookReviewCounts();
  }, [fetchBookReviewCounts]);

  return {
    bookReviewCounts,
    loading,
    error,
    getReviewCountForBook,
    refreshCounts,
  };
};

export default useBookReviewStats;
